import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, TrendingUp, FileText, Target, Award, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import WhatsAppFloatingButton, { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { usePageMeta } from "@/hooks/usePageMeta";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { toast } from "@/hooks/use-toast";
import heroImage from "@/assets/hero-avaliacao.jpg";

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Olá, Paulo! Gostaria de uma avaliação do meu imóvel."
)}`;

const reasons = [
  {
    icon: TrendingUp,
    title: "Evita perdas financeiras",
    text: "Subavaliar significa deixar dinheiro na mesa; superavaliar afasta compradores e alonga o tempo de venda.",
  },
  {
    icon: Shield,
    title: "Segurança jurídica e financeira",
    text: "Você negocia com confiança, sabendo exatamente o valor de mercado do seu imóvel.",
  },
  {
    icon: FileText,
    title: "Base sólida para decisões",
    text: "Vender, alugar, usar como garantia de crédito ou planejar um inventário e partilha.",
  },
  {
    icon: Target,
    title: "Posicionamento no alto padrão",
    text: "Pequenas diferenças de preço mudam completamente o perfil de comprador que chega até o imóvel.",
  },
];

const steps = [
  {
    title: "Você preenche o formulário",
    text: "Informe os dados básicos do imóvel — leva menos de dois minutos.",
  },
  {
    title: "Paulo Miranda entra em contato",
    text: "Para entender o imóvel e agendar a vistoria, presencial ou por vídeo, conforme sua preferência.",
  },
  {
    title: "Vistoria técnica",
    text: "Características, estado de conservação, acabamentos, vista, posição solar e diferenciais são registrados.",
  },
  {
    title: "Você recebe o laudo (PTAM)",
    text: "Com valor de mercado sugerido, comparativo com imóveis semelhantes na região e recomendações de posicionamento.",
  },
];

const factors = [
  "Localização, bairro e proximidade a pontos de interesse (praia, metrô, comércio)",
  "Metragem, planta e distribuição dos ambientes",
  "Estado de conservação e padrão de acabamento",
  "Vista, posição solar e andar",
  "Vagas de garagem, lazer do condomínio e infraestrutura do prédio",
  "Documentação do imóvel (matrícula, IPTU, situação condominial)",
  "Preço de imóveis comparáveis vendidos ou alugados recentemente na região",
  "Tempo médio de venda de imóveis com o mesmo perfil",
  "Momento do mercado e nível de demanda pelo tipo de imóvel",
];

const differentials = [
  "Avaliação conduzida pessoalmente pelo corretor Paulo Miranda, CRECI-RJ 83590, especialista em Zona Sul e Barra da Tijuca.",
  "Laudo técnico de avaliação (PTAM) organizado e ilustrado — não apenas um número.",
  "Conhecimento local de lançamentos e revenda em Ipanema, Leblon, Copacabana, Botafogo, Lagoa, Flamengo, Barra e Recreio.",
  "Sem custo e sem compromisso de venda.",
];

const faq = [
  {
    question: "A avaliação tem algum custo?",
    answer:
      "Não. A avaliação inicial é 100% gratuita e sem compromisso de fechar negócio com a Top Class Imóveis.",
  },
  {
    question: "Preciso estar em casa para a vistoria?",
    answer:
      "O ideal é acompanhar a vistoria presencialmente, mas em alguns casos é possível fazer uma pré-avaliação por vídeo ou fotos, com ajuste posterior.",
  },
  {
    question: "Quanto tempo leva para receber o resultado?",
    answer: "Em geral, o laudo é entregue em até 3 a 5 dias úteis após a vistoria.",
  },
  {
    question: "A avaliação serve para inventário ou financiamento?",
    answer:
      "A avaliação de mercado ajuda a embasar esses processos, mas para fins formais de inventário ou financiamento bancário pode ser exigido um laudo com ART de engenheiro ou arquiteto — nesse caso, Paulo Miranda indica um profissional parceiro.",
  },
  {
    question: "Vocês avaliam imóveis fora da Zona Sul e Barra da Tijuca?",
    answer:
      "O foco de atuação é Zona Sul (Ipanema, Leblon, Copacabana, Botafogo, Lagoa, Flamengo) e Barra da Tijuca/Recreio, mas outras regiões podem ser consultadas pelo WhatsApp.",
  },
  {
    question: "Depois da avaliação, sou obrigado a anunciar com vocês?",
    answer:
      "Não. Você recebe a avaliação e decide o que fazer com ela. Se quiser anunciar, terá todo o suporte da Top Class Imóveis.",
  },
];

const selectClass =
  "w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body text-foreground";

const Avaliacao = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  usePageMeta({
    title: "Avaliação de Imóvel Gratuita no Rio | TopClass Imóveis",
    description:
      "Avaliação técnica gratuita do seu imóvel na Zona Sul e Barra da Tijuca. Laudo PTAM com valor de mercado, comparáveis e recomendações. CRECI-RJ 83590.",
    keywords:
      "Avaliação de imóvel Rio de Janeiro, Quanto vale meu imóvel, Avaliação gratuita imóvel Zona Sul, Laudo PTAM Rio, Avaliar apartamento Barra da Tijuca",
    ogTitle: "Quanto vale o seu imóvel? | Avaliação gratuita TopClass Imóveis",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: "Autorize o contato para continuar.", variant: "destructive" });
      return;
    }
    const fd = new FormData(e.currentTarget);
    const get = (k: string) => String(fd.get(k) || "").trim();

    setLoading(true);
    try {
      const { error } = await submitCrmLead({
        name: get("name"),
        phone: get("phone"),
        email: get("email"),
        propertyName: "Avaliação de imóvel (proprietário)",
        temperature: "quente",
        notes: [
          `Endereço: ${get("address") || "não informado"}`,
          `Tipo: ${get("type")}`,
          `Finalidade: ${get("purpose")}`,
          `Metragem: ${get("area") || "-"} m²`,
          `Quartos: ${get("bedrooms") || "-"} | Vagas: ${get("garage") || "-"}`,
          `Melhor horário: ${get("bestTime")}`,
          get("notes") && `Observações: ${get("notes")}`,
        ].filter(Boolean) as string[],
      });
      if (error) throw error;
      sessionStorage.setItem("tc_return_to", "/avaliacao");
      navigate(THANK_YOU.avaliacao);
    } catch {
      toast({
        title: "Não foi possível enviar",
        description: "Tente novamente ou fale com Paulo Miranda no WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="relative">
          <img
            src={heroImage}
            alt="Sala de apartamento de alto padrão com vista para a Lagoa Rodrigo de Freitas"
            width={1920}
            height={1088}
            className="w-full h-[52vh] md:h-[70vh] object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-primary/70" />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4 max-w-3xl text-center md:text-left">
              <span className="inline-block font-body text-xs tracking-[0.25em] text-accent mb-4">
                CRECI-RJ 83590
              </span>
              <h1 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight">
                Quanto vale o seu imóvel?
              </h1>
              <p className="font-body text-white/85 mt-4 text-base md:text-lg">
                Avaliação técnica gratuita para proprietários que querem vender ou alugar com
                segurança na Zona Sul e Barra da Tijuca.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7 justify-center md:justify-start">
                <Button variant="gold" size="lg" asChild>
                  <a href="#formulario" title="Ir para o formulário de avaliação gratuita">Solicitar minha avaliação gratuita</a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/40 hover:bg-white/20"
                  asChild
                >
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" title="Falar com Paulo Miranda no WhatsApp">
                    Falar com Paulo Miranda agora
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Por que importa */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
              Por que uma avaliação correta importa
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {reasons.map((r) => (
                <div key={r.title} className="border border-border rounded-lg p-6 bg-card">
                  <r.icon className="h-7 w-7 text-accent mb-4" />
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{r.title}</h3>
                  <p className="font-body text-sm text-muted-foreground">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Como funciona */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground text-center mb-10">
              Como funciona
            </h2>
            <ol className="space-y-6">
              {steps.map((s, i) => (
                <li key={s.title} className="flex gap-4">
                  <span className="flex-shrink-0 h-10 w-10 rounded-full bg-accent text-primary font-display font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-primary-foreground">
                      {s.title}
                    </h3>
                    <p className="font-body text-sm text-primary-foreground/75 mt-1">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Metodologia */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
              Metodologia e fatores considerados
            </h2>
            <blockquote className="border-l-4 border-accent pl-5 font-body text-muted-foreground italic">
              A avaliação segue o método comparativo direto de dados de mercado — o mais utilizado no
              mercado imobiliário residencial — que analisa imóveis semelhantes, vendidos ou
              anunciados recentemente na mesma região. Dependendo do caso (imóveis atípicos,
              terrenos, salas comerciais), também podem ser aplicados os métodos involutivo,
              evolutivo ou de capitalização de renda.
            </blockquote>
            <h3 className="font-display text-xl font-bold text-foreground mt-10 mb-4">
              Fatores considerados na avaliação
            </h3>
            <ul className="grid gap-3 md:grid-cols-2">
              {factors.map((f) => (
                <li key={f} className="font-body text-sm text-muted-foreground flex gap-2">
                  <span className="text-accent">•</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Diferenciais da avaliação Top Class
            </h2>
            <ul className="space-y-4">
              {differentials.map((d) => (
                <li key={d} className="flex gap-3 font-body text-muted-foreground">
                  <Award className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Formulário */}
        <section id="formulario" className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="rounded-xl p-6 md:p-9 bg-gradient-to-b from-[#3a3a3a] to-[#2b2b2b]">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Solicite sua avaliação gratuita
              </h2>
              <p className="font-body text-sm text-white/70 mb-6">
                Preencha os dados do imóvel e receba o contato de Paulo Miranda.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-white/90" htmlFor="name">Nome completo *</Label>
                  <Input id="name" name="name" required className="mt-1" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-white/90" htmlFor="phone">WhatsApp *</Label>
                    <Input id="phone" name="phone" required className="mt-1" placeholder="(21) 99999-9999" />
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="email">E-mail *</Label>
                    <Input id="email" name="email" type="email" required className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/90" htmlFor="address">
                    Endereço do imóvel (rua, número, bairro) *
                  </Label>
                  <Input id="address" name="address" required className="mt-1" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-white/90" htmlFor="type">Tipo de imóvel</Label>
                    <select id="type" name="type" className={`${selectClass} mt-1`} defaultValue="Apartamento">
                      <option>Apartamento</option>
                      <option>Casa</option>
                      <option>Cobertura</option>
                      <option>Sala comercial</option>
                      <option>Terreno</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="purpose">Finalidade</Label>
                    <select id="purpose" name="purpose" className={`${selectClass} mt-1`} defaultValue="Quero vender">
                      <option>Quero vender</option>
                      <option>Quero alugar</option>
                      <option>Só quero saber o valor</option>
                      <option>Inventário ou partilha</option>
                    </select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-white/90" htmlFor="area">Metragem (m²)</Label>
                    <Input id="area" name="area" type="number" min="0" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="bedrooms">Quartos</Label>
                    <Input id="bedrooms" name="bedrooms" type="number" min="0" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="garage">Vagas</Label>
                    <Input id="garage" name="garage" type="number" min="0" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/90" htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    rows={3}
                    className="mt-1"
                    placeholder="Diferenciais, reforma, mobília, vista..."
                  />
                </div>
                <div>
                  <Label className="text-white/90" htmlFor="bestTime">Melhor horário para contato</Label>
                  <select id="bestTime" name="bestTime" className={`${selectClass} mt-1`} defaultValue="Manhã">
                    <option>Manhã</option>
                    <option>Tarde</option>
                    <option>Noite</option>
                  </select>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(Boolean(v))}
                    className="mt-1 border-white/40"
                  />
                  <Label htmlFor="consent" className="font-body text-xs text-white/80 leading-relaxed">
                    Autorizo o contato da Top Class Imóveis Premium com as informações acima.
                  </Label>
                </div>
                <Button variant="gold" size="lg" className="w-full" type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Quero minha avaliação gratuita
                </Button>
                <p className="font-body text-xs text-white/60 text-center italic">
                  Resposta em até 24h úteis. Sem custo, sem compromisso.
                </p>
              </form>
            </div>
          </div>
        </section>

        <FaqSection title="Perguntas Frequentes — Avaliação" items={faq} scriptId="avaliacao" />

        {/* CTA final */}
        <section className="py-16 bg-primary text-center">
          <div className="container mx-auto px-4 max-w-2xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
              Descubra agora quanto vale o seu imóvel
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="gold" size="lg" asChild>
                <a href="#formulario" title="Ir para o formulário de avaliação gratuita">Solicitar avaliação gratuita</a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10"
                asChild
              >
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" title="Falar com Paulo Miranda no WhatsApp">
                  Falar no WhatsApp (+55 21 97354-8387)
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppFloatingButton />
      </main>
    </div>
  );
};

export default Avaliacao;
